/**
 * Edits a placeholder chip's field name and formatter pipeline. Opened by clicking a chip, or
 * Enter on a selected one (cdm/placeholderEdit.ts); anchored to the chip with fixed positioning
 * so canvas zoom and scrolling need no extra math. Apply writes one transaction; Esc, Cancel or a
 * click outside discards.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react'
import type { Editor } from '@tiptap/core'
import { colors, fontSize, radius, shadow, spacing } from '../../styles/theme'
import { FORMATTERS, FORMATTER_GROUPS, formatterDef } from '../../cdm/formatters'
import { applyPlaceholderEdit, placeholderNameError, setPlaceholderEditHandler } from '../../cdm/placeholderEdit'
import type { FormatterSpec } from '../../cdm/types'

interface Row {
  id: number
  name: string
  arg: string
  /** The args as loaded, kept verbatim unless the arg field is edited. */
  original: { name: string; arg: string; args: string[] } | null
}

const POPOVER_WIDTH = 340

let nextRowId = 1

function toRows(formatters: FormatterSpec[] | null): Row[] {
  return (formatters ?? []).map(f => {
    const arg = f.args.join(',')
    return { id: nextRowId++, name: f.name, arg, original: { name: f.name, arg, args: f.args } }
  })
}

function toSpecs(rows: Row[]): FormatterSpec[] {
  return rows.map(r => {
    if (r.original && r.original.name === r.name && r.original.arg === r.arg) return { name: r.name, args: [...r.original.args] }
    const takesArg = formatterDef(r.name)?.arg !== undefined || !formatterDef(r.name)
    return { name: r.name, args: takesArg && r.arg.trim() !== '' ? [r.arg] : [] }
  })
}

export default function PlaceholderPopover({ editor }: { editor: Editor | null }) {
  const [pos, setPos] = useState<number | null>(null)
  const [rawName, setRawName] = useState('')
  const [rows, setRows] = useState<Row[]>([])
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null)
  const boxRef = useRef<HTMLFormElement>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  const close = useCallback((refocus: boolean) => {
    setPos(null)
    setAnchor(null)
    if (refocus) editor?.view.focus()
  }, [editor])

  useEffect(() => {
    if (!editor) return
    setPlaceholderEditHandler(editor, p => {
      const node = editor.state.doc.nodeAt(p)
      if (!node || node.type.name !== 'placeholder') return
      setRawName(node.attrs.rawName as string)
      setRows(toRows(node.attrs.formatters as FormatterSpec[] | null))
      setPos(p)
    })
    return () => setPlaceholderEditHandler(editor, null)
  }, [editor])

  const reposition = useCallback(() => {
    if (!editor || pos === null) return
    const dom = editor.view.nodeDOM(pos)
    if (!(dom instanceof HTMLElement)) return
    const r = dom.getBoundingClientRect()
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 8))
    // Below the chip, or above it when there is no room below.
    const h = boxRef.current?.offsetHeight ?? 0
    const below = r.bottom + 6
    const top = below + h > window.innerHeight - 8 && r.top - 6 - h >= 8 ? r.top - 6 - h : below
    setAnchor({ top, left })
  }, [editor, pos])

  useLayoutEffect(() => {
    if (pos === null) return
    reposition()
    // The box's height is only known once it has rendered (hidden) at its first anchor.
    const raf = window.requestAnimationFrame(reposition)
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.cancelAnimationFrame(raf)
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [pos, reposition])

  useLayoutEffect(() => { reposition() }, [rows.length, reposition])

  useEffect(() => {
    if (pos === null) return
    nameRef.current?.focus()
    nameRef.current?.select()
    const onDown = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) close(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [pos, close])

  // The chip can disappear under the popover (undo, collaborative edit, a delete elsewhere).
  useEffect(() => {
    if (!editor || pos === null) return
    const check = () => { if (editor.state.doc.nodeAt(pos)?.type.name !== 'placeholder') close(false) }
    editor.on('update', check)
    return () => { editor.off('update', check) }
  }, [editor, pos, close])

  if (!editor || pos === null) return null

  const nameError = placeholderNameError(rawName)
  const update = (id: number, patch: Partial<Row>) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)))
  const move = (i: number, d: -1 | 1) => setRows(rs => {
    const j = i + d
    if (j < 0 || j >= rs.length) return rs
    const next = [...rs]
    ;[next[i], next[j]] = [next[j], next[i]]
    return next
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (nameError || pos === null || !editor) return
    applyPlaceholderEdit(editor, pos, { rawName, formatters: toSpecs(rows) })
    close(true)
  }

  const input: CSSProperties = { padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.sm, minWidth: 0, boxSizing: 'border-box' }
  const iconBtn = (enabled = true): CSSProperties => ({
    width: 24, height: 24, padding: 0, border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: colors.surface,
    color: enabled ? colors.textSecondary : colors.textMuted, cursor: enabled ? 'pointer' : 'default', fontSize: fontSize.xs, flex: '0 0 auto',
  })

  return (
    <form
      ref={boxRef}
      role="dialog"
      aria-label="Edit field"
      onSubmit={submit}
      onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true) } }}
      style={{
        position: 'fixed', top: anchor?.top ?? 0, left: anchor?.left ?? 0, width: POPOVER_WIDTH, zIndex: 30,
        visibility: anchor ? 'visible' : 'hidden', padding: spacing[3], background: colors.surface,
        border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.lg, fontSize: fontSize.sm, color: colors.text,
        display: 'flex', flexDirection: 'column', gap: spacing.sm, boxSizing: 'border-box',
      }}
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: colors.textSecondary }}>
        Field name
        <input
          ref={nameRef}
          value={rawName}
          onChange={e => setRawName(e.target.value)}
          spellCheck={false}
          aria-invalid={nameError !== null}
          style={{ ...input, fontFamily: 'monospace' }}
        />
      </label>
      {nameError && rawName !== '' && <div role="alert" style={{ color: colors.error, fontSize: fontSize.xs }}>{nameError}</div>}

      <div style={{ color: colors.textSecondary }}>Formatters</div>
      {rows.length === 0 && <div style={{ color: colors.textMuted, fontSize: fontSize.xs }}>None — the value is inserted as-is.</div>}
      <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r, i) => {
          const def = formatterDef(r.name)
          const known = def !== undefined
          const arg = def?.arg
          return (
            <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <select
                aria-label={`Formatter ${i + 1}`}
                value={known ? def.name : r.name}
                onChange={e => {
                  const d = formatterDef(e.target.value)
                  update(r.id, { name: e.target.value, arg: d?.arg?.defaultValue ?? '' })
                }}
                style={{ ...input, flex: '1 1 40%' }}
              >
                {!known && <option value={r.name}>{r.name}</option>}
                {FORMATTER_GROUPS.map(g => (
                  <optgroup key={g} label={g}>
                    {FORMATTERS.filter(f => f.group === g).map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {(arg || !known) && (arg?.choices ? (
                <select aria-label={`${arg.label} for ${r.name}`} value={r.arg} onChange={e => update(r.id, { arg: e.target.value })} style={{ ...input, flex: '1 1 35%' }}>
                  {!arg.choices.includes(r.arg) && <option value={r.arg}>{r.arg || '—'}</option>}
                  {arg.choices.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : (
                <input
                  aria-label={`${arg?.label ?? 'Arguments'} for ${r.name}`}
                  value={r.arg}
                  placeholder={arg?.placeholder}
                  onChange={e => update(r.id, { arg: e.target.value })}
                  spellCheck={false}
                  style={{ ...input, flex: '1 1 35%', width: 0 }}
                />
              ))}
              <button type="button" aria-label={`Move ${r.name} up`} disabled={i === 0} onClick={() => move(i, -1)} style={iconBtn(i > 0)}>↑</button>
              <button type="button" aria-label={`Move ${r.name} down`} disabled={i === rows.length - 1} onClick={() => move(i, 1)} style={iconBtn(i < rows.length - 1)}>↓</button>
              <button type="button" aria-label={`Remove ${r.name}`} onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))} style={iconBtn()}>×</button>
            </li>
          )
        })}
      </ol>
      <button
        type="button"
        onClick={() => setRows(rs => [...rs, { id: nextRowId++, name: 'upper', arg: '', original: null }])}
        style={{ alignSelf: 'flex-start', padding: '4px 10px', border: `1px dashed ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.primary, cursor: 'pointer', fontSize: fontSize.sm }}
      >
        + Add formatter
      </button>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: spacing.sm }}>
        <button type="button" onClick={() => close(true)} style={{ padding: '4px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, background: 'none', color: colors.textSecondary, cursor: 'pointer', fontSize: fontSize.sm }}>Cancel</button>
        <button type="submit" disabled={nameError !== null} style={{ padding: '4px 12px', border: 'none', borderRadius: radius.sm, background: nameError ? colors.border : colors.primary, color: '#fff', cursor: nameError ? 'default' : 'pointer', fontSize: fontSize.sm, fontWeight: 600 }}>Apply</button>
      </div>
    </form>
  )
}
